import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Truck,
  Package,
  Boxes,
  Route,
  Zap,
  Settings,
  ArrowRight,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
  }),
};

const services = [
  { icon: Package, title: "Stykgods & pakketransport", text: "Sikker og effektiv transport af mindre forsendelser og pakker – enkeltvis eller i større mængder." },
  { icon: Boxes, title: "Palleforsendelser", text: "Pålidelig håndtering og transport af palleforsendelser til erhverv i hele Danmark." },
  { icon: Route, title: "Distribution", text: "Landsdækkende distribution med et effektivt rutenetværk, så dine varer når frem til tiden." },
  { icon: Truck, title: "Faste kørselsaftaler", text: "Fleksible og faste kørselsaftaler tilpasset din virksomheds daglige transportbehov." },
  { icon: Zap, title: "Ekspres & hastelevering", text: "Når det haster – hurtig og direkte levering med fokus på punktlighed og sikkerhed." },
  { icon: Settings, title: "Skræddersyede løsninger", text: "Vi udvikler transportløsninger, der matcher netop dine krav, fragttyper og tidsplaner." },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand">Ydelser</span>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Fleksible transport- og logistikløsninger
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
            Uanset om du har brug for enkeltforsendelser, faste aftaler eller
            landsdækkende distribution, leverer SB Logistik professionelle
            løsninger med kvalitet og service i fokus.
          </p>
        </div>
      </section>

      {/* Services grid */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              data-testid={`service-card-${i}`}
              className="group rounded-sm border border-slate-200 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-slate-300"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-sm bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <s.icon className="h-7 w-7" />
              </span>
              <h3 className="mt-6 font-heading text-xl font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 md:flex-row md:items-center">
          <h2 className="max-w-xl font-heading text-2xl font-semibold tracking-tight text-white md:text-3xl">
            Har du en transportopgave? Lad os finde den rette løsning.
          </h2>
          <Link
            data-testid="services-cta-button"
            to="/kontakt"
            className="inline-flex shrink-0 items-center gap-2 rounded-sm bg-brand px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-hover"
          >
            Indhent et uforpligtende tilbud <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
